import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import Icon3D from '../components/Icon3D';
import {
  adminTutoringDashboard,
  adminListSessions,
  adminFlaggedSessions,
  adminApprovePayout,
  adminRejectSession,
  adminListReports,
  browseTutors,
} from '../api/endpoints';
import {
  BarChart3, Users, AlertTriangle, Flag, CheckCircle, XCircle,
  DollarSign, Clock, Shield, Eye, ChevronLeft, ChevronRight,
  Loader2, Search, Filter, Star, UserCheck, UserX, RefreshCw,
  TrendingUp, Calendar, GraduationCap, FileWarning,
} from 'lucide-react';

const AdminTutoringPage = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Dashboard state
  const [dashboardData, setDashboardData] = useState(null);

  // Sessions state
  const [sessions, setSessions] = useState([]);
  const [sessionsTotal, setSessionsTotal] = useState(0);
  const [sessionsPage, setSessionsPage] = useState(1);
  const [sessionStatusFilter, setSessionStatusFilter] = useState('');

  // Flagged state
  const [flaggedSessions, setFlaggedSessions] = useState([]);

  // Reports state
  const [reports, setReports] = useState([]);
  const [reportsTotal, setReportsTotal] = useState(0);
  const [reportsPage, setReportsPage] = useState(1);
  const [reportStatusFilter, setReportStatusFilter] = useState('');

  // Tutor approval state
  const [pendingTutors, setPendingTutors] = useState([]);
  const [approvedTutors, setApprovedTutors] = useState([]);

  // Action loading
  const [actionLoading, setActionLoading] = useState({});

  const loadDashboard = useCallback(async () => {
    try {
      const res = await adminTutoringDashboard();
      setDashboardData(res.data);
    } catch (err) {
      setError('Failed to load dashboard data');
    }
  }, []);

  const loadSessions = useCallback(async () => {
    try {
      const params = { page: sessionsPage, limit: 20 };
      if (sessionStatusFilter) params.status = sessionStatusFilter;
      const res = await adminListSessions(params);
      setSessions(res.data.sessions || []);
      setSessionsTotal(res.data.total || 0);
    } catch (err) {
      setError('Failed to load sessions');
    }
  }, [sessionsPage, sessionStatusFilter]);

  const loadFlagged = useCallback(async () => {
    try {
      const res = await adminFlaggedSessions();
      setFlaggedSessions(res.data.sessions || []);
    } catch (err) {
      setError('Failed to load flagged sessions');
    }
  }, []);

  const loadReports = useCallback(async () => {
    try {
      const params = { page: reportsPage, limit: 20 };
      if (reportStatusFilter) params.status = reportStatusFilter;
      const res = await adminListReports(params);
      setReports(res.data.reports || []);
      setReportsTotal(res.data.total || 0);
    } catch (err) {
      setError('Failed to load reports');
    }
  }, [reportsPage, reportStatusFilter]);

  const loadTutors = useCallback(async () => {
    try {
      // Load pending tutors
      const pendingRes = await browseTutors({ approved: 'false', limit: 50 });
      setPendingTutors(pendingRes.data.tutors || []);
      // Load recently approved
      const approvedRes = await browseTutors({ limit: 20, sort: 'newest' });
      setApprovedTutors(approvedRes.data.tutors || []);
    } catch (err) {
      // Non-critical — browseTutors may not support approved filter
      console.warn('Could not load tutor lists:', err);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError('');
      try {
        if (activeTab === 'dashboard') await loadDashboard();
        else if (activeTab === 'sessions') await loadSessions();
        else if (activeTab === 'flagged') await loadFlagged();
        else if (activeTab === 'reports') await loadReports();
        else if (activeTab === 'tutors') await loadTutors();
      } catch (err) {
        // handled in individual loaders
      }
      setLoading(false);
    };
    loadData();
  }, [activeTab, loadDashboard, loadSessions, loadFlagged, loadReports, loadTutors]);

  // Refresh sessions/flagged when filters change
  useEffect(() => {
    if (activeTab === 'sessions') loadSessions();
  }, [sessionsPage, sessionStatusFilter, activeTab, loadSessions]);

  useEffect(() => {
    if (activeTab === 'reports') loadReports();
  }, [reportsPage, reportStatusFilter, activeTab, loadReports]);

  const handleApprovePayout = async (sessionId) => {
    setActionLoading(prev => ({ ...prev, [sessionId]: 'approve' }));
    try {
      await adminApprovePayout(sessionId);
      // Refresh flagged list
      await loadFlagged();
      await loadDashboard();
    } catch (err) {
      alert('Failed to approve payout: ' + (err.response?.data?.detail || err.message));
    }
    setActionLoading(prev => ({ ...prev, [sessionId]: null }));
  };

  const handleRejectSession = async (sessionId) => {
    if (!window.confirm('Are you sure you want to reject this session? The student will be refunded.')) return;
    setActionLoading(prev => ({ ...prev, [sessionId]: 'reject' }));
    try {
      await adminRejectSession(sessionId);
      await loadFlagged();
      await loadDashboard();
    } catch (err) {
      alert('Failed to reject session: ' + (err.response?.data?.detail || err.message));
    }
    setActionLoading(prev => ({ ...prev, [sessionId]: null }));
  };

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
    { id: 'sessions', label: 'Sessions', icon: Calendar },
    { id: 'flagged', label: 'Flagged', icon: Flag, badge: dashboardData?.sessions?.flagged },
    { id: 'reports', label: 'Reports', icon: AlertTriangle, badge: dashboardData?.reports?.pending },
    { id: 'tutors', label: 'Tutor Approval', icon: UserCheck },
  ];

  const statusColors = {
    pending_payment: 'bg-yellow-100 text-yellow-700',
    booked: 'bg-blue-100 text-blue-700',
    confirmed: 'bg-indigo-100 text-indigo-700',
    in_progress: 'bg-cyan-100 text-cyan-700',
    completed: 'bg-green-100 text-green-700',
    disputed: 'bg-red-100 text-red-700',
    cancelled: 'bg-gray-100 text-gray-600',
    refunded: 'bg-cyan-100 text-cyan-700',
  };

  const reportStatusColors = {
    pending: 'bg-yellow-100 text-yellow-700',
    investigating: 'bg-blue-100 text-blue-700',
    resolved: 'bg-green-100 text-green-700',
    dismissed: 'bg-gray-100 text-gray-600',
  };

  const formatDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-AU', {
      day: 'numeric', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  // ── Dashboard Tab ──────────────────────────────────────────────
  const renderDashboard = () => {
    if (!dashboardData) return null;
    const { tutors, sessions: s, reports: r, revenue } = dashboardData;

    const statCards = [
      { label: 'Total Tutors', value: tutors?.total || 0, icon: Users, colour: 'blue' },
      { label: 'Approved', value: tutors?.approved || 0, icon: UserCheck, colour: 'green' },
      { label: 'Pending Approval', value: tutors?.pending_approval || 0, icon: Clock, colour: 'amber' },
      { label: 'Total Sessions', value: s?.total || 0, icon: Calendar, colour: 'indigo' },
      { label: 'Completed', value: s?.completed || 0, icon: CheckCircle, colour: 'green' },
      { label: 'Flagged', value: s?.flagged || 0, icon: Flag, colour: 'red' },
      { label: 'Pending Reports', value: r?.pending || 0, icon: AlertTriangle, colour: 'orange' },
    ];

    return (
      <div className="space-y-6">
        {/* Stat Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
          {statCards.map((card) => (
              <div key={card.label} className="bg-white rounded-xl border border-slate-200 p-4" style={{ boxShadow: 'var(--shadow-card)' }}>
                <div className="mb-2"><Icon3D icon={card.icon} colour={card.colour} size="sm" /></div>
                <p className="text-2xl font-bold text-slate-900">{card.value}</p>
                <p className="text-xs text-slate-500 mt-1">{card.label}</p>
              </div>
            ))}
        </div>

        {/* Revenue Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-xl p-6">
            <div className="flex items-center gap-2 mb-2">
              <DollarSign className="w-5 h-5 text-green-600" />
              <span className="text-sm font-medium text-green-700">Platform Revenue</span>
            </div>
            <p className="text-3xl font-bold text-slate-900">
              ${(revenue?.total_revenue || 0).toFixed(2)}
            </p>
            {revenue?.total_gst > 0 && (
              <p className="text-xs text-slate-500 mt-1">GST collected: ${revenue.total_gst.toFixed(2)}</p>
            )}
          </div>

          <div className="bg-gradient-to-br from-blue-50 to-cyan-50 border border-blue-200 rounded-xl p-6">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-5 h-5 text-blue-600" />
              <span className="text-sm font-medium text-blue-700">Total Payouts</span>
            </div>
            <p className="text-3xl font-bold text-slate-900">
              ${(revenue?.total_payouts || 0).toFixed(2)}
            </p>
            <p className="text-xs text-slate-500 mt-1">Paid to tutors</p>
          </div>

          <div className="bg-gradient-to-br from-purple-50 to-indigo-50 border border-purple-200 rounded-xl p-6">
            <div className="flex items-center gap-2 mb-2">
              <Shield className="w-5 h-5 text-purple-600" />
              <span className="text-sm font-medium text-purple-700">Net Revenue</span>
            </div>
            <p className="text-3xl font-bold text-slate-900">
              ${((revenue?.total_revenue || 0) - (revenue?.total_gst || 0)).toFixed(2)}
            </p>
            <p className="text-xs text-slate-500 mt-1">After GST</p>
          </div>
        </div>

        {/* Quick actions */}
        <div className="bg-white border border-slate-200 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-slate-900 mb-4">Quick Actions</h3>
          <div className="flex flex-wrap gap-3">
            {(s?.flagged || 0) > 0 && (
              <button
                onClick={() => setActiveTab('flagged')}
                className="flex items-center gap-2 px-4 py-2 bg-red-50 text-red-700 border border-red-200 rounded-lg hover:bg-red-100 transition"
              >
                <Flag className="w-4 h-4" />
                Review {s.flagged} flagged session{s.flagged !== 1 ? 's' : ''}
              </button>
            )}
            {(r?.pending || 0) > 0 && (
              <button
                onClick={() => setActiveTab('reports')}
                className="flex items-center gap-2 px-4 py-2 bg-cyan-50 text-cyan-700 border border-cyan-200 rounded-lg hover:bg-cyan-100 transition"
              >
                <AlertTriangle className="w-4 h-4" />
                {r.pending} pending report{r.pending !== 1 ? 's' : ''}
              </button>
            )}
            {(tutors?.pending_approval || 0) > 0 && (
              <button
                onClick={() => setActiveTab('tutors')}
                className="flex items-center gap-2 px-4 py-2 bg-amber-50 text-amber-700 border border-amber-200 rounded-lg hover:bg-amber-100 transition"
              >
                <UserCheck className="w-4 h-4" />
                {tutors.pending_approval} tutor{tutors.pending_approval !== 1 ? 's' : ''} awaiting approval
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ── Sessions Tab ───────────────────────────────────────────────
  const renderSessions = () => {
    const totalPages = Math.ceil(sessionsTotal / 20);

    return (
      <div className="space-y-4">
        {/* Filters */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-slate-400" />
            <select
              value={sessionStatusFilter}
              onChange={(e) => { setSessionStatusFilter(e.target.value); setSessionsPage(1); }}
              className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
            >
              <option value="">All Statuses</option>
              <option value="pending_payment">Pending Payment</option>
              <option value="booked">Booked</option>
              <option value="confirmed">Confirmed</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="disputed">Disputed</option>
              <option value="cancelled">Cancelled</option>
              <option value="refunded">Refunded</option>
            </select>
          </div>
          <span className="text-sm text-slate-500">{sessionsTotal} session{sessionsTotal !== 1 ? 's' : ''}</span>
        </div>

        {/* Session Table */}
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-slate-600">Session</th>
                  <th className="text-left px-4 py-3 font-medium text-slate-600">Tutor</th>
                  <th className="text-left px-4 py-3 font-medium text-slate-600">Student</th>
                  <th className="text-left px-4 py-3 font-medium text-slate-600">Subject</th>
                  <th className="text-left px-4 py-3 font-medium text-slate-600">Date</th>
                  <th className="text-left px-4 py-3 font-medium text-slate-600">Amount</th>
                  <th className="text-left px-4 py-3 font-medium text-slate-600">Status</th>
                  <th className="text-left px-4 py-3 font-medium text-slate-600">Flags</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {sessions.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-4 py-8 text-center text-slate-400">No sessions found</td>
                  </tr>
                ) : sessions.map((session) => (
                  <tr key={session.id} className="hover:bg-slate-50 transition">
                    <td className="px-4 py-3">
                      <span className="font-mono text-xs text-slate-500">{session.id?.slice(0, 8)}...</span>
                    </td>
                    <td className="px-4 py-3 text-slate-700">{session.tutor_id?.slice(0, 8)}...</td>
                    <td className="px-4 py-3 text-slate-700">{session.student_id?.slice(0, 8)}...</td>
                    <td className="px-4 py-3 text-slate-700">{session.subject || '—'}</td>
                    <td className="px-4 py-3 text-slate-500 text-xs">{formatDate(session.scheduled_at)}</td>
                    <td className="px-4 py-3 font-medium text-slate-900">${session.student_total?.toFixed(2) || '—'}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[session.status] || 'bg-gray-100 text-gray-600'}`}>
                        {session.status?.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {session.flagged_for_review && (
                        <Flag className="w-4 h-4 text-red-500" />
                      )}
                      {(session.fraud_flags || []).length > 0 && (
                        <span className="text-xs text-red-500 ml-1">
                          {session.fraud_flags.length}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-4">
            <button
              onClick={() => setSessionsPage(p => Math.max(1, p - 1))}
              disabled={sessionsPage <= 1}
              className="p-2 rounded-lg border border-slate-200 disabled:opacity-40 hover:bg-slate-50"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-sm text-slate-600">Page {sessionsPage} of {totalPages}</span>
            <button
              onClick={() => setSessionsPage(p => Math.min(totalPages, p + 1))}
              disabled={sessionsPage >= totalPages}
              className="p-2 rounded-lg border border-slate-200 disabled:opacity-40 hover:bg-slate-50"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    );
  };

  // ── Flagged Tab ────────────────────────────────────────────────
  const renderFlagged = () => {
    if (flaggedSessions.length === 0) {
      return (
        <div className="text-center py-12">
          <Shield className="w-12 h-12 text-green-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-700 mb-2">All Clear</h3>
          <p className="text-sm text-slate-500">No flagged sessions requiring review.</p>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <p className="text-sm text-slate-500">
          {flaggedSessions.length} session{flaggedSessions.length !== 1 ? 's' : ''} flagged for review
        </p>

        {flaggedSessions.map((session) => (
          <div key={session.id} className="bg-white border border-red-200 rounded-xl p-5 space-y-4">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Flag className="w-4 h-4 text-red-500" />
                  <span className="font-semibold text-slate-900">Session {session.id?.slice(0, 8)}...</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[session.status] || 'bg-gray-100'}`}>
                    {session.status?.replace('_', ' ')}
                  </span>
                </div>
                <p className="text-sm text-slate-500">
                  {session.subject} • {session.duration_minutes} min • ${session.session_price?.toFixed(2)}
                </p>
              </div>
              <span className="text-xs text-slate-400">{formatDate(session.scheduled_at)}</span>
            </div>

            {/* Fraud Flags */}
            {(session.fraud_flags || []).length > 0 && (
              <div className="bg-red-50 rounded-lg p-3">
                <p className="text-xs font-semibold text-red-700 mb-2">Fraud Flags:</p>
                <div className="flex flex-wrap gap-2">
                  {session.fraud_flags.map((flag, i) => (
                    <span key={i} className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full font-medium">
                      {flag.replace('_', ' ')}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Session Details */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div>
                <p className="text-slate-400 text-xs">Tutor</p>
                <p className="text-slate-700 font-medium">{session.tutor_id?.slice(0, 12)}...</p>
              </div>
              <div>
                <p className="text-slate-400 text-xs">Student</p>
                <p className="text-slate-700 font-medium">{session.student_id?.slice(0, 12)}...</p>
              </div>
              <div>
                <p className="text-slate-400 text-xs">Tutor Payout</p>
                <p className="text-slate-700 font-medium">${session.tutor_payout_amount?.toFixed(2) || '—'}</p>
              </div>
              <div>
                <p className="text-slate-400 text-xs">Payout Eligible</p>
                <p className="text-slate-700 font-medium">{formatDate(session.payout_eligible_at)}</p>
              </div>
            </div>

            {/* IP Comparison */}
            {(session.student_ip || session.tutor_ip) && (
              <div className="text-xs text-slate-500 bg-slate-50 rounded-lg p-2">
                <span className="font-medium">IPs:</span>{' '}
                Student: {session.student_ip || 'N/A'} | Tutor: {session.tutor_ip || 'N/A'}
                {session.student_ip && session.tutor_ip && session.student_ip === session.tutor_ip && (
                  <span className="ml-2 text-red-600 font-semibold">⚠ SAME IP</span>
                )}
              </div>
            )}

            {/* Dispute Info */}
            {session.dispute_reason && (
              <div className="bg-cyan-50 rounded-lg p-3">
                <p className="text-xs font-semibold text-cyan-700">Dispute Reason:</p>
                <p className="text-sm text-cyan-600 mt-1">{session.dispute_reason}</p>
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center gap-3 pt-2 border-t border-slate-100">
              <button
                onClick={() => handleApprovePayout(session.id)}
                disabled={!!actionLoading[session.id]}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition text-sm font-medium"
              >
                {actionLoading[session.id] === 'approve' ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <CheckCircle className="w-4 h-4" />
                )}
                Approve Payout
              </button>
              <button
                onClick={() => handleRejectSession(session.id)}
                disabled={!!actionLoading[session.id]}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition text-sm font-medium"
              >
                {actionLoading[session.id] === 'reject' ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <XCircle className="w-4 h-4" />
                )}
                Reject & Refund
              </button>
            </div>
          </div>
        ))}
      </div>
    );
  };

  // ── Reports Tab ────────────────────────────────────────────────
  const renderReports = () => {
    const totalPages = Math.ceil(reportsTotal / 20);

    return (
      <div className="space-y-4">
        {/* Filters */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-slate-400" />
            <select
              value={reportStatusFilter}
              onChange={(e) => { setReportStatusFilter(e.target.value); setReportsPage(1); }}
              className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
            >
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="investigating">Investigating</option>
              <option value="resolved">Resolved</option>
              <option value="dismissed">Dismissed</option>
            </select>
          </div>
          <span className="text-sm text-slate-500">{reportsTotal} report{reportsTotal !== 1 ? 's' : ''}</span>
        </div>

        {/* Reports List */}
        {reports.length === 0 ? (
          <div className="text-center py-12">
            <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-700 mb-2">No Reports</h3>
            <p className="text-sm text-slate-500">No reports matching the current filter.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {reports.map((report) => (
              <div key={report.id} className="bg-white border border-slate-200 rounded-xl p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-cyan-500" />
                    <span className="font-semibold text-slate-900 capitalize">{report.category?.replace('_', ' ')}</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${reportStatusColors[report.status] || 'bg-gray-100'}`}>
                      {report.status}
                    </span>
                  </div>
                  <span className="text-xs text-slate-400">{formatDate(report.created_at)}</span>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm mb-3">
                  <div>
                    <p className="text-slate-400 text-xs">Reporter</p>
                    <p className="text-slate-700">{report.reporter_role} ({report.reporter_id?.slice(0, 8)}...)</p>
                  </div>
                  <div>
                    <p className="text-slate-400 text-xs">Reported</p>
                    <p className="text-slate-700">{report.reported_role} ({report.reported_id?.slice(0, 8)}...)</p>
                  </div>
                  <div>
                    <p className="text-slate-400 text-xs">Session</p>
                    <p className="text-slate-700 font-mono text-xs">{report.session_id?.slice(0, 12) || 'N/A'}...</p>
                  </div>
                </div>

                {report.description && (
                  <div className="bg-slate-50 rounded-lg p-3 text-sm text-slate-600">
                    {report.description}
                  </div>
                )}

                {report.admin_notes && (
                  <div className="mt-2 bg-blue-50 rounded-lg p-3 text-sm text-blue-700">
                    <span className="font-medium">Admin notes:</span> {report.admin_notes}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-4">
            <button
              onClick={() => setReportsPage(p => Math.max(1, p - 1))}
              disabled={reportsPage <= 1}
              className="p-2 rounded-lg border border-slate-200 disabled:opacity-40 hover:bg-slate-50"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-sm text-slate-600">Page {reportsPage} of {totalPages}</span>
            <button
              onClick={() => setReportsPage(p => Math.min(totalPages, p + 1))}
              disabled={reportsPage >= totalPages}
              className="p-2 rounded-lg border border-slate-200 disabled:opacity-40 hover:bg-slate-50"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    );
  };

  // ── Tutor Approval Tab ─────────────────────────────────────────
  const renderTutors = () => {
    return (
      <div className="space-y-6">
        {/* Pending Approval */}
        <div>
          <h3 className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
            <Clock className="w-5 h-5 text-amber-500" />
            Pending Approval ({pendingTutors.length})
          </h3>

          {pendingTutors.length === 0 ? (
            <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center">
              <CheckCircle className="w-8 h-8 text-green-400 mx-auto mb-2" />
              <p className="text-sm text-green-700">No tutors awaiting approval.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {pendingTutors.map((tutor) => (
                <div key={tutor.id} className="bg-white border border-amber-200 rounded-xl p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="font-semibold text-slate-900">{tutor.bio?.slice(0, 100) || 'No bio'}</h4>
                      <p className="text-sm text-slate-500 mt-1">
                        {tutor.education_level} • {tutor.subjects?.join(', ') || 'No subjects'}
                      </p>
                    </div>
                    <span className="text-xs text-slate-400">{formatDate(tutor.created_at)}</span>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm mb-3">
                    <div>
                      <p className="text-slate-400 text-xs">Rate</p>
                      <p className="text-slate-700 font-medium">${tutor.hourly_rate}/hr</p>
                    </div>
                    <div>
                      <p className="text-slate-400 text-xs">Languages</p>
                      <p className="text-slate-700">{tutor.languages?.join(', ') || '—'}</p>
                    </div>
                    <div>
                      <p className="text-slate-400 text-xs">Stripe Status</p>
                      <p className={`font-medium ${tutor.stripe_connect_status === 'active' ? 'text-green-600' : 'text-amber-600'}`}>
                        {tutor.stripe_connect_status || 'pending'}
                      </p>
                    </div>
                    <div>
                      <p className="text-slate-400 text-xs">Stripe Verified Name</p>
                      <p className="text-slate-700">{tutor.stripe_verified_name || 'Not yet verified'}</p>
                    </div>
                  </div>

                  {/* Credential uploads */}
                  {(tutor.credential_uploads || []).length > 0 && (
                    <div className="bg-slate-50 rounded-lg p-3 mb-3">
                      <p className="text-xs font-semibold text-slate-600 mb-2">Uploaded Credentials:</p>
                      <div className="flex flex-wrap gap-2">
                        {tutor.credential_uploads.map((cred, i) => (
                          <a
                            key={i}
                            href={cred.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-xs text-cyan-600 hover:bg-cyan-50 transition"
                          >
                            <Eye className="w-3 h-3" />
                            {cred.type?.replace('_', ' ')}
                            {cred.verified && <CheckCircle className="w-3 h-3 text-green-500" />}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Name mismatch warning */}
                  {tutor.stripe_verified_name && tutor.credential_uploads?.length > 0 && (
                    <div className="text-xs mb-3">
                      <span className="text-slate-500">Name match: </span>
                      <span className="font-medium text-slate-700">
                        Stripe says "{tutor.stripe_verified_name}"
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recently Approved */}
        <div>
          <h3 className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
            <UserCheck className="w-5 h-5 text-green-500" />
            Active Tutors ({approvedTutors.length})
          </h3>

          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium text-slate-600">Tutor</th>
                    <th className="text-left px-4 py-3 font-medium text-slate-600">Subjects</th>
                    <th className="text-left px-4 py-3 font-medium text-slate-600">Rate</th>
                    <th className="text-left px-4 py-3 font-medium text-slate-600">Sessions</th>
                    <th className="text-left px-4 py-3 font-medium text-slate-600">Rating</th>
                    <th className="text-left px-4 py-3 font-medium text-slate-600">Trust</th>
                    <th className="text-left px-4 py-3 font-medium text-slate-600">Stripe</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {approvedTutors.map((tutor) => (
                    <tr key={tutor.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3 text-slate-700">{tutor.id?.slice(0, 10)}...</td>
                      <td className="px-4 py-3 text-slate-600 text-xs">{(tutor.subjects || []).slice(0, 3).join(', ')}</td>
                      <td className="px-4 py-3 font-medium text-slate-900">${tutor.hourly_rate}/hr</td>
                      <td className="px-4 py-3 text-slate-600">{tutor.total_sessions || 0}</td>
                      <td className="px-4 py-3">
                        {tutor.average_rating ? (
                          <span className="flex items-center gap-1">
                            <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />
                            {tutor.average_rating.toFixed(1)}
                          </span>
                        ) : '—'}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          tutor.trust_tier === 'trusted' ? 'bg-green-100 text-green-700' :
                          tutor.trust_tier === 'established' ? 'bg-blue-100 text-blue-700' :
                          'bg-slate-100 text-slate-600'
                        }`}>
                          {tutor.trust_tier || 'new'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs font-medium ${
                          tutor.stripe_connect_status === 'active' ? 'text-green-600' : 'text-amber-600'
                        }`}>
                          {tutor.stripe_connect_status || 'pending'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ── Main Render ────────────────────────────────────────────────
  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-3">
              <Icon3D icon={GraduationCap} colour="purple" size="md" />
              Tutoring Admin
            </h1>
            <p className="text-sm text-slate-500 mt-1">Manage tutors, sessions, payouts, and reports</p>
          </div>
          <button
            onClick={() => {
              setLoading(true);
              const reload = async () => {
                await loadDashboard();
                if (activeTab === 'sessions') await loadSessions();
                if (activeTab === 'flagged') await loadFlagged();
                if (activeTab === 'reports') await loadReports();
                if (activeTab === 'tutors') await loadTutors();
                setLoading(false);
              };
              reload();
            }}
            className="flex items-center gap-2 px-4 py-2 border border-slate-200 rounded-lg text-sm text-slate-600 hover:bg-slate-50 transition"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-slate-100 p-1 rounded-xl mb-6 overflow-x-auto">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition whitespace-nowrap relative overflow-hidden text-white"
                style={activeTab === tab.id ? {
                  background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
                  boxShadow: 'var(--shadow-3d-btn)',
                } : {
                  background: 'linear-gradient(135deg, #94a3b8 0%, #64748b 100%)',
                  boxShadow: 'var(--shadow-3d-btn)',
                }}
              >
                {activeTab === tab.id && (
                  <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full" style={{ animation: 'shimmer 2.5s infinite' }} />
                )}
                <Icon className="w-4 h-4 relative z-10" />
                <span className="relative z-10">{tab.label}</span>
                {tab.badge > 0 && (
                  <span className="ml-1 px-1.5 py-0.5 bg-red-500 text-white text-xs rounded-full font-bold min-w-[20px] text-center relative z-10">
                    {tab.badge}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Error */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl flex items-center gap-2 text-sm text-red-700">
            <FileWarning className="w-4 h-4 flex-shrink-0" />
            {error}
          </div>
        )}

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-cyan-500" />
          </div>
        ) : (
          <>
            {activeTab === 'dashboard' && renderDashboard()}
            {activeTab === 'sessions' && renderSessions()}
            {activeTab === 'flagged' && renderFlagged()}
            {activeTab === 'reports' && renderReports()}
            {activeTab === 'tutors' && renderTutors()}
          </>
        )}
      </div>
    </Layout>
  );
};

export default AdminTutoringPage;
